import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '../../../../theme';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import ButtonLabel from '../typography/ButtonType';

export const BUTTON_TYPE = {
    primary: 'primary',
    secondary: 'secondary',
    text: 'text',
    outlined: 'outlined',
    link: 'link'
};

export type ButtonType = keyof typeof BUTTON_TYPE;

export const BUTTON_SIZE = {
    default: 'default',
    medium: 'medium',
    small: 'small'
};

export interface ButtonBaseProps extends HTMLAttributes<HTMLButtonElement> {
    /**
     * TODO: We need to refactor Button component as `color = "text"` confuses responsibility of the `color` property.
     * For example we can use MUI approach and divide this property to `variant` and `color` with single responsibility.
     * https://mui.com/material-ui/react-button/#basic-button
     */
    color?: ButtonType;
    icon?: ReactNode;
    isLoading?: boolean;
    size?: 'default' | 'medium' | 'small';
    withLabelIcon?: boolean;
    withLeftIcon?: boolean;
    withRightIcon?: boolean;
    width?: string;
}

export interface ButtonProps {
    className?: string;
    disabled?: boolean;
    label?: string | ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const BUTTON_COLORS = {
    [BUTTON_TYPE.text]: theme.semanticColor.primary,
    [BUTTON_TYPE.outlined]: theme.semanticColor.primary,
    [BUTTON_TYPE.link]: theme.semanticColor.primary
};

const BUTTON_COLORS_ON_HOVER = {
    [BUTTON_TYPE.text]: theme.semanticColor.hoverPrimary,
    [BUTTON_TYPE.outlined]: theme.semanticColor.hoverPrimary,
    [BUTTON_TYPE.link]: theme.semanticColor.hoverPrimary
};

const BUTTON_BG_COLORS = {
    [BUTTON_TYPE.primary]: theme.semanticColor.primary,
    [BUTTON_TYPE.secondary]: theme.semanticColor.secondary,
    [BUTTON_TYPE.text]: 'transparent',
    [BUTTON_TYPE.outlined]: 'transparent',
    [BUTTON_TYPE.link]: 'transparent'
};

const BUTTON_BG_COLORS_ON_HOVER = {
    [BUTTON_TYPE.primary]: theme.semanticColor.hoverPrimary,
    [BUTTON_TYPE.secondary]: theme.semanticColor.hoverSecondary,
    [BUTTON_TYPE.text]: 'transparent',
    [BUTTON_TYPE.outlined]: 'transparent',
    [BUTTON_TYPE.link]: 'transparent'
};

const BUTTON_BG_COLORS_ON_ACTIVE = {
    [BUTTON_TYPE.primary]: theme.semanticColor.primary,
    [BUTTON_TYPE.secondary]: theme.semanticColor.hoverSecondary,
    [BUTTON_TYPE.text]: 'transparent',
    [BUTTON_TYPE.outlined]: 'transparent',
    [BUTTON_TYPE.link]: 'transparent'
};

const BUTTON_BG_COLORS_ON_FOCUS = {
    [BUTTON_TYPE.primary]: theme.semanticColor.primary,
    [BUTTON_TYPE.secondary]: theme.semanticColor.secondary,
    [BUTTON_TYPE.text]: 'transparent',
    [BUTTON_TYPE.outlined]: 'transparent',
    [BUTTON_TYPE.link]: 'transparent'
};

const BUTTON_PADDING = {
    [BUTTON_SIZE.default]: `${ theme.size.size_10 } ${ theme.size.size_20 }`,
    [BUTTON_SIZE.medium]: `${ theme.size.size_15 } ${ theme.size.size_25 }`,
    [BUTTON_SIZE.small]: `${ theme.size.size_10 } ${ theme.size.size_15 }`,

    [`${ BUTTON_SIZE.default }-${ BUTTON_TYPE.link }`]: `${ theme.size.size_5 } 0`
};

const ICON_PADDING = {
    [BUTTON_SIZE.default]: theme.size.size_20,
    [BUTTON_SIZE.medium]: theme.size.size_15,
    [BUTTON_SIZE.small]: theme.size.size_10
};

type ButtonTypeOrString = ButtonType | string | undefined;

const getButtonColor = (buttonType: ButtonTypeOrString) =>
    (!buttonType ? theme.color.white : BUTTON_COLORS[buttonType] || theme.color.white);
const getButtonColorOnHover = (buttonType: ButtonTypeOrString) =>
    (!buttonType ? theme.color.white : BUTTON_COLORS_ON_HOVER[buttonType] || theme.color.white);
const getButtonBgColor = (buttonType: ButtonTypeOrString) =>
    (!buttonType
        ? theme.semanticColor.primary
        : BUTTON_BG_COLORS[buttonType] || theme.semanticColor.primary);
const getButtonBgColorOnHover = (buttonType: ButtonTypeOrString) =>
    (!buttonType
        ? theme.semanticColor.primary
        : BUTTON_BG_COLORS_ON_HOVER[buttonType] || theme.semanticColor.primary);
const getButtonBgColorOnActive = (buttonType: ButtonTypeOrString) =>
    (!buttonType
        ? theme.semanticColor.primary
        : BUTTON_BG_COLORS_ON_ACTIVE[buttonType] || theme.semanticColor.primary);
const getButtonBgColorOnFocus = (buttonType: ButtonTypeOrString) =>
    (!buttonType
        ? theme.semanticColor.primary
        : BUTTON_BG_COLORS_ON_FOCUS[buttonType] || theme.semanticColor.primary);

const getButtonPadding = (buttonSize: string | undefined = 'default', buttonType: ButtonType | undefined) => (
    (buttonType ? BUTTON_PADDING[`${ buttonSize }-${ buttonType }`] : null) ||
    BUTTON_PADDING[buttonSize]
);

const getIconPadding = (buttonSize: string | undefined) =>
    (!buttonSize ? theme.size.size_20 : ICON_PADDING[buttonSize] || theme.size.size_20);

const Button = forwardRef<HTMLButtonElement, ButtonBaseProps & ButtonProps>(({
    color = 'primary',
    disabled = false,
    icon,
    isLoading = false,
    label = '',
    onClick = () => undefined,
    size = 'default',
    withLabelIcon = false,
    withLeftIcon = false,
    withRightIcon = false,
    width,
    ...restProps
}, ref) => {
    return (
        <Root { ...restProps }
            color={ color }
            disabled={ disabled }
            isLoading={ isLoading }
            onClick={ (event: React.MouseEvent<HTMLButtonElement>) => !disabled && onClick(event) }
            ref={ ref }
            size={ size }
            withLabelIcon={ withLabelIcon }
            width={ width }
        >
            { withLeftIcon && <Icon color={ color }>{ icon }</Icon> }

            <StyledButtonLabel
                color={ color }
                isLoading={ isLoading }
                size={ size }
                withLeftIcon={ withLeftIcon }
                withRightIcon={ withRightIcon }
            >
                { label }
            </StyledButtonLabel>

            { isLoading && <StyledLoadingSpinner /> }

            { withRightIcon && <Icon color={ color }>{ icon }</Icon> }

            { withLabelIcon && <Icon large = { !label }
                color={ color }>{ icon }</Icon> }
        </Root>
    );
});

export default styled(Button)``;

type IconProps = ButtonBaseProps & {
    large?: boolean;
}

const Icon = styled(ButtonLabel)<IconProps>`
    height: ${ ({ theme, large }) => (large ? theme.size.size_25 : theme.size.size_20) };
    width: ${ ({ theme, large }) => (large ? theme.size.size_25 : theme.size.size_20) };

    svg {
        height: ${ ({ theme, large }) => (large ? theme.size.size_25 : theme.size.size_20) };
        width: ${ ({ theme, large }) => (large ? theme.size.size_25 : theme.size.size_20) };

        path {
            fill: ${ ({ color }) => getButtonColor(color) };
        }
    }
`;

const StyledButtonLabel = styled(ButtonLabel)<ButtonBaseProps>`
    color: ${ ({ color, isLoading }) => (isLoading ? 'transparent' : getButtonColor(color)) };
    margin-left: ${ ({ size, theme, withLeftIcon }) => {
        if (withLeftIcon) {
            return size === 'default' ? theme.size.size_15 : theme.size.size_10;
        } else {
            return 0;
        }
    } };
    margin-right: ${ ({ size, theme, withRightIcon }) => {
        if (withRightIcon) {
            return size === 'default' ? theme.size.size_15 : theme.size.size_10;
        } else {
            return 0;
        }
    } };
    white-space: nowrap;
    ${ ({ color }) => color === 'text' && 'text-transform: uppercase;' }
    ${ ({ color }) => color === 'link' && 'text-decoration: underline;' }
`;

const buttonBorderStyle = ({ color }: ButtonBaseProps) => {
    switch (color) {
        case 'outlined':
            return css`
                border: 1px solid ${ ({ theme }) => theme.semanticColor.border };

                &:hover {
                    border-color: ${ ({ theme }) => theme.semanticColor.primary };
                }

                &:focus-visible {
                    border-color: ${ ({ theme }) => theme.semanticColor.focus };
                    outline: 1px solid ${ ({ theme }) => theme.semanticColor.focus };
                }

                &:active {
                    border-color: ${ getButtonBgColorOnActive(color) };
                }

                &:disabled {
                    border-color: ${ ({ theme }) => theme.color.grey.grey_40 };
                }
            `;
        default:
            return css`
                border: 1px solid ${ getButtonBgColor(color) };

                &:hover {
                    border-color: ${ getButtonBgColorOnHover(color) };
                }

                &:active {
                    border-color: ${ getButtonBgColorOnActive(color) };
                }

                &:focus-visible {
                    border-color: ${ getButtonBgColorOnFocus(color) };
                }

                &:disabled {
                    border-color: ${ ({ theme }) => theme.semanticColor.disabled };
                }
            `;
    }
};

const disabledBackgroundStyle = ({ color }: ButtonBaseProps) => {
    if (color !== 'outlined') {
        return css`background-color: ${ ({ theme }) => theme.semanticColor.disabled };`;
    }

    return css`background-color: ${ ({ theme }) => theme.color.white };`;
};

const Root = styled.button<ButtonBaseProps>`
    align-items: center;
    background-color: ${ ({ color }) => getButtonBgColor(color) };
    border-radius: ${ ({ color, withLabelIcon, theme }) =>
        (withLabelIcon && (color === BUTTON_TYPE.text)
            ? '50%'
            : theme.radius.radius_5) };
    cursor: ${ ({ isLoading }) => (isLoading ? 'not-allowed' : 'pointer') };
    display: flex;
    justify-content: center;
    outline: none;
    padding: ${ ({ size, withLabelIcon, color }) =>
        (withLabelIcon ? getIconPadding(size) : getButtonPadding(size, color)) };
    pointer-events: ${ ({ isLoading }) => (isLoading ? 'none' : 'auto') };
    position: relative;
    transition: all 150ms;
    width: ${ ({ width }) => (width ? width : 'auto') };

    ${ buttonBorderStyle }

    &:hover {
        background-color: ${ ({ color }) => getButtonBgColorOnHover(color) };
        box-shadow: none;

        ${ StyledButtonLabel } {
            color: ${ ({ color, isLoading }) => (isLoading ? 'transparent' : getButtonColorOnHover(color)) };
        }

        ${ Icon } {
            svg {
                path {
                    fill: ${ ({ color }) => getButtonColorOnHover(color) };
                }
            }
        }
    }

    &:focus-visible {
        background-color: ${ ({ color }) => getButtonBgColorOnFocus(color) };

        ${ StyledButtonLabel } {
            color: ${ ({ color, isLoading }) => (isLoading ? 'transparent' : getButtonColor(color)) };
        }

        ${ Icon } {
            svg {
                path {
                    fill: ${ ({ color }) => getButtonColor(color) };
                }
            }
        }
    }

    &:active {
        background-color: ${ ({ color }) => getButtonBgColorOnActive(color) };
        box-shadow: none;

        ${ StyledButtonLabel } {
            color: ${ ({ color, isLoading }) => (isLoading ? 'transparent' : getButtonColor(color)) };
        }

        ${ Icon } {
            svg {
                path {
                    fill: ${ ({ color }) => getButtonColor(color) };
                }
            }
        }
    }

    &:disabled {
        box-shadow: none;
        cursor: default;

        ${ disabledBackgroundStyle }

        ${ StyledButtonLabel } {
            color: ${ ({ theme }) => theme.semanticColor.textDisabled };
        }

        ${ Icon } {
            svg {
                path {
                    fill: ${ ({ theme }) => theme.semanticColor.textDisabled };
                }
            }
        }
    }
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
    position: absolute;
`;
